import type { MediaItem } from '@/types'

/** A photograph with a crop focus, for frames whose shape differs from the original. */
export const at = (item: MediaItem, position: string): MediaItem => ({ ...item, position })

/**
 * The full photo library. Campaign images first, then the generated course
 * photography, then the few lifestyle photos that still earn their place.
 * To replace an image, save it under a NEW filename and update it here.
 */
export const library = {
    clubhouseSunset: {
        src: '/images/campaign/hero-clubhouse-sunset.png',
        alt: 'The clubhouse terrace and a row of golf carts beside a green at sunset, olive trees in front and the sea and mountains beyond'
    },
    coastPeninsula: {
        src: '/images/campaign/coast-peninsula.png',
        alt: 'Fairways and bunkers running along a pine-covered peninsula above the sea at sunset, mountains beyond'
    },
    coursePanorama: {
        src: '/images/campaign/course-panorama.png',
        alt: 'Sculpted greens and white bunkers among umbrella pines, with the bay and mountains glowing at sunset'
    },
    greenAtSunset: {
        src: '/images/campaign/green-at-sunset.png',
        alt: 'A flagstick on an immaculate green above the bay as the sun sets behind the mountains'
    },
    membersWalking: {
        src: '/images/campaign/members-walking.png',
        alt: 'Two golfers carrying their bags down a fairway toward the bay at sunset'
    },
    clubhouseArrival: {
        src: '/images/campaign/clubhouse-arrival.png',
        alt: 'A golfer walking past the cart court toward the lit clubhouse terrace at sunset'
    },
    readingThePutt: {
        src: '/images/campaign/reading-the-putt.png',
        alt: 'A golfer crouching to read a putt beside the flag, the bay and mountains behind'
    },
    ironAndBall: {
        src: '/images/campaign/iron-and-ball.png',
        alt: 'An iron resting behind a ball on dewy turf, fairways and the bay softly out of focus'
    },
    fairwayPanorama: {
        src: '/images/campaign/fairway-panorama.png',
        alt: 'A wide fairway framed by bunkers and a still lake, rising toward a villa among the pines'
    },
    terraceDining: {
        src: '/images/campaign/terrace-dining.png',
        alt: 'Guests dining on the glass-fronted clubhouse terrace at golden hour, carts lined up below'
    },
    lagoonHoles: {
        src: '/images/campaign/lagoon-holes.png',
        alt: 'Fairways curving around a lagoon beneath umbrella pines, the bay shining at sunset'
    },
    clubhouseCarts: {
        src: '/images/campaign/clubhouse-carts.png',
        alt: 'The modern clubhouse with its long flat roof and glowing glass, a row of carts and an olive tree in front'
    },
    cliffTerrace: {
        src: '/images/campaign/cliff-terrace.png',
        alt: 'The clubhouse terrace on the cliff edge, with coastal fairways stretching along the sea below'
    },
    goldenCourse: {
        src: '/images/golf-hero.jpg',
        alt: 'A coastal golf course at golden hour, its fairway curving through dunes and umbrella pines toward the ocean'
    },
    aerialCoast: {
        src: '/images/golf-aerial-coast.jpg',
        alt: 'Fairways, bunkers and a green seen from directly above, running along sea cliffs and Atlantic surf'
    },
    holePines: {
        src: '/images/golf-hole-pines.jpg',
        alt: 'A fairway bending left around a grove of ancient umbrella pines in early morning light'
    },
    holeQuarry: {
        src: '/images/golf-hole-quarry.jpg',
        alt: 'A par three green framed by the cut rock walls of an old stone quarry'
    },
    holeHeath: {
        src: '/images/golf-hole-heath.jpg',
        alt: 'A long fairway through flowering coastal heath rolling toward the ocean'
    },
    holeMirror: {
        src: '/images/golf-hole-mirror.jpg',
        alt: 'A peninsula green on a still reservoir at dawn, its flag reflected in the water'
    },
    holeReturn: {
        src: '/images/golf-hole-return.jpg',
        alt: 'The final green below a low rammed earth clubhouse with a long timber roof, glowing at dusk'
    },
    firstLight: {
        src: '/images/golf-first-light.jpg',
        alt: 'A lone golfer carrying a bag along a dewy fairway at first light, with sea mist and pines beyond'
    },
    academyRange: {
        src: '/images/golf-academy-range.jpg',
        alt: 'A golfer mid-swing in a covered range bay at dusk, with the floodlit range stretching beyond'
    },
    twoGolfers: {
        src: '/images/golf-members.jpg',
        alt: 'Two golfers walking side by side down a wide fairway at sunrise'
    },
    platedScallop: {
        src: '/images/dining-plate-2.jpg',
        alt: 'A seared scallop with prawns and fresh leaves on a white plate'
    },
    platedSeafood: {
        src: '/images/dining-plate-1.jpg',
        alt: 'White fish and prawns with an orange sauce and herbs on a textured plate'
    },
    stonePavilion: {
        src: '/images/clubhouse-3.jpg',
        alt: 'A low stone pavilion with wide windows set into the dunes'
    },
    lodge: {
        src: '/images/lodge-1.jpg',
        alt: 'A timber lodge on a stone base, set into a grassy hillside'
    },
    reservoirJetty: {
        src: '/images/nature-swim-1.jpg',
        alt: 'A timber jetty reaching into the still, misty reservoir'
    },
    reservoirMorning: {
        src: '/images/nature-swim-2.jpg',
        alt: 'Morning mist over the reservoir with reflected trees'
    },
    pineSlope: {
        src: '/images/hole-pines-2.jpg',
        alt: 'Tall pines along a sunlit grassy slope with mist rising behind'
    },
    clifftopGrasses: {
        src: '/images/hole-heath-2.jpg',
        alt: 'Pale grasses on the clifftop above a calm blue ocean'
    }
} satisfies Record<string, MediaItem>

/** Where each photograph sits on the home page and in the shared sections. */
export const media = {
    hero: at(library.clubhouseSunset, '55% center'),
    landscapeTall: library.firstLight,
    landscapeWide: at(library.goldenCourse, 'center 55%'),
    landscapeDetail: at(library.coursePanorama, '62% 74%'),
    aerial: library.aerialCoast,
    holePines: library.holePines,
    holeQuarry: library.holeQuarry,
    holeHeath: library.holeHeath,
    holeMirror: library.holeMirror,
    holeReturn: library.holeReturn,
    clubhouseExterior: at(library.clubhouseCarts, '30% center'),
    clubhouseWide: at(library.cliffTerrace, '30% center'),
    clubhouseTall: at(library.clubhouseArrival, '36% center'),
    academyRange: library.academyRange,
    academyShortGame: at(library.greenAtSunset, '82% center'),
    academyPutting: at(library.readingThePutt, '38% center'),
    academyStudio: at(library.ironAndBall, '52% center'),
    academyShortNine: at(library.fairwayPanorama, '58% center'),
    membership: at(library.membersWalking, 'center 42%'),
    diningPlate: library.platedScallop,
    diningTerrace: at(library.terraceDining, '76% center'),
    diningCellar: library.platedSeafood,
    diningHalfway: library.stonePavilion,
    diningLodges: library.lodge,
    eventPrivate: at(library.twoGolfers, 'center 60%'),
    natureHero: at(library.lagoonHoles, 'center 60%'),
    natureReservoir: library.reservoirJetty,
    natureTrails: library.pineSlope,
    natureBeach: library.clifftopGrasses
} satisfies Record<string, MediaItem>
